import { LonaIcons, SparkleIcon } from "@tento-ui/ui/icons";
import type { Point } from "@tento-core/point";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type Option, RowId, RowLocalId } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import { SheetViewModel } from "@tento-lona";
import { DateFragment, DateTime, NaiveDate, Utc } from "@tento-chrono";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { StickerDrag, StickerMoveDrag } from "@tento-ui/components/sticker/sticker-drag";
import { DragDrop } from "@tento-ui/drag-drop";
import { Flags } from "@tento-ui/flags";
import {
  StickerGestureItem,
  type StickerItemDelegate,
  type StickerSlot,
} from "./source-sticker/sticker-gesture-item.ts";
import { StickerFixedChildren } from "./source-sticker/sticker-fixed-children.ts";
import {
  type StickerDropZone,
  StickerDragGhost,
} from "./source-sticker/sticker-drag-ghost.ts";
import type { ItemActionContext } from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { parseStickerData } from "./source-sticker/sticker-cell-data.ts";

import { SourceRow } from "@tento-lona/sheets/platform";
import { liveVariantOf } from "../../sheet/live-variant.ts";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";
/** Debug-only: outline a sticker row's day band while a palette sticker
 *  hovers it, to confirm the drop target resolves to the right row/column.
 *  Off by default — the drop stamps a real sticker, which is the actual
 *  user-facing feedback; the dashed outline is a development aid. Toggle
 *  via the `debug-flag:sticker-drop-hint` localStorage key. */
const SHOW_DROP_HINT = new Flags.Debug(
  "sticker-drop-hint",
  false,
  "Outline sticker rows while dragging a palette sticker",
);

/** Page (document) coords → viewport coords for a `position: fixed` chip /
 *  a `getBoundingClientRect`-relative resolve. */
function toClient(page: { x: number; y: number }): { x: number; y: number } {
  return { x: page.x - window.scrollX, y: page.y - window.scrollY };
}

/** The source-row plugin for stickers placed on the calendar. */
export class StickerSourcePlugin implements RenderPlugin.SourceRow<
  HTMLElement,
  SheetLegend.DefaultRow
> {
  readonly role = "source";
  readonly id = "sticker";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(SparkleIcon);

  loadData = null;
  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  #delegate: Option<StickerItemDelegate> = null;

  /** App-scoped drag controller, shared with the sidebar sticker palette
   *  (injected at plugin construction). The sticker row's layout band
   *  registers a drop target on it so a palette sticker can be dropped
   *  onto the sheet. */
  #dragController: Option<DragDrop.Controller> = null;
  /** Layout bands currently registered as drop targets. A week view has
   *  one band per (row, day), so each is registered separately and a drop
   *  resolves against the day column it sits in. Stale (disconnected)
   *  bands from a prior render are pruned on the next registration and
   *  skipped by the controller's hit-test meanwhile. */
  readonly #dropTargets = new Set<HTMLElement>();
  /** Live geometry/time context for each registered drop band. Refreshed from
   *  the layout bind hook because a band element can be recycled while its
   *  `windowed` day fragment changes. */
  readonly #dropZones = new WeakMap<HTMLElement, StickerDropZone>();

  /** Per-row RowCellCache change subscriptions (create/move/delete →
   *  re-emit overlays). Keyed by row id string; subscribed once. */
  readonly #rowSubs = new Map<string, () => void>();

  /** Overlay artwork elements registered as drag sources — the sticker's
   *  move/details handle. Drawn in the fixed-child layer (so they escape
   *  the column clip and stay grabbable where they overhang), they each
   *  publish a `StickerMoveDrag` payload. Pruned when their element
   *  leaves the DOM on the next render pass. */
  readonly #overlaySources = new Set<HTMLElement>();
  /** Live slot per registered overlay element, refreshed on every
   *  re-render so a drag/tap reads current data even though the source is
   *  registered once. */
  readonly #overlaySlots = new WeakMap<HTMLElement, StickerSlot>();
  /** Overlay currently raised because its context menu is open. */
  #contextMenuOverlay: Option<HTMLElement> = null;

  /** Sheet handle, captured in `makeFixedChildren` (the only place the
   *  sticker plugin sees it), for the row-change overlay re-emit. */
  #handle: Option<SheetDelegate> = null;

  /** The in-flight drag-ghost state machine, shared by an existing-sticker
   *  MOVE (overlay drag source) and a palette CREATE (band drop target).
   *  It pushes a raised ghost via `handle.fixedChild` — a LIGHT, targeted
   *  update that tracks the pointer WITHOUT a whole-sheet re-emit. */
  readonly #ghost = new StickerDragGhost(() => this.#delegate);

  constructor(public readonly makeRowOptions: RowOptionsElement.Factory) {}

  setDelegate(delegate: StickerItemDelegate): void {
    this.#delegate = delegate;
  }

  setDragController(controller: DragDrop.Controller): void {
    this.#dragController = controller;
  }

  /** Register (or re-point) a sticker row's layout band as a drag target.
   *  Each live band (one per day in a week view) is its own target so the
   *  drag resolves against the day column it sits in. A re-layout makes a
   *  fresh band element, so stale (disconnected) bands are pruned here and
   *  skipped by the controller's hit-test meanwhile. */
  #registerDropTarget(
    $layout: HTMLElement,
    context: CalendarSubcolumn.LayoutBindContext,
  ): void {
    const controller = this.#dragController;
    if (!controller) return;
    this.#dropZones.set($layout, {
      windowed: context.windowed,
      bandRowId: context.bandRowId,
      column: context.column,
      bandRect: () => $layout.getBoundingClientRect(),
      columnRect: () => $layout.getBoundingClientRect(),
    });
    for (const $prev of this.#dropTargets) {
      if (!$prev.isConnected) {
        controller.unregisterTarget($prev);
        this.#dropTargets.delete($prev);
      }
    }
    if (this.#dropTargets.has($layout)) return;
    // The band is a drop target for BOTH a palette CREATE and an existing
    // MOVE. `bindLayout` refreshes this element's drop zone with the current
    // row/column/day context, so the ghost can render an in-sheet fixed-child
    // preview and resolve the persisted position from the same geometry.
    controller.registerTarget($layout, {
      canAccept: (payload) =>
        payload.is(StickerDrag.KIND) || payload.is(StickerMoveDrag.KIND),
      onEnter: () => {
        if (SHOW_DROP_HINT.value) {
          $layout.style.outline = "2px dashed var(--blue)";
          $layout.style.outlineOffset = "-2px";
        }
      },
      onLeave: () => {
        $layout.style.outline = "";
        $layout.style.outlineOffset = "";
        this.#ghost.clearSheetPreview();
      },
      onMove: (e) => {
        const dropZone = this.#dropZones.get($layout) ?? null;
        if (!dropZone) return;
        const client = toClient(e.page);
        const createPayload = e.payload.get(StickerDrag.KIND);
        if (createPayload != null) {
          this.#ghost.previewCreate(
            this.#handle,
            dropZone,
            createPayload.stickerId,
            createPayload.effect,
            createPayload.effectVariant ?? null,
            client,
          );
        } else {
          this.#ghost.previewMove(this.#handle, dropZone, client);
        }
      },
      onDrop: (e) => {
        $layout.style.outline = "";
        $layout.style.outlineOffset = "";
        this.#ghost.clearSheetPreview();
        // Persist at the exact drop point — a palette CREATE makes a new
        // sticker, an existing MOVE relocates one. The day column owns the
        // x-fraction + time mapping; the band ($layout) owns the y frame
        // the resting overlay is positioned in.
        const client = toClient(e.page);
        const dropZone = this.#dropZones.get($layout) ?? null;
        const createPayload = e.payload.get(StickerDrag.KIND);
        if (createPayload != null) {
          this.#ghost.dropCreate(
            createPayload.stickerId,
            createPayload.effect,
            dropZone,
            client,
          );
        } else {
          this.#ghost.dropMove(dropZone, client);
        }
      },
    });
    this.#dropTargets.add($layout);
  }

  /** Register a sticker overlay's artwork as a drag source — the sole
   *  move/details handle. Idempotent per element; the live slot is
   *  refreshed each render so a drag/tap reads current data even though
   *  the source is registered once. Disconnected sources are pruned on
   *  the next overlay pass. */
  #registerOverlaySource(
    $inner: HTMLElement,
    slot: StickerSlot,
    row: SheetRow.Joined.Handle,
    fixedContext: CalendarSubcolumn.FixedChildrenContext,
    resolveColumn: (date: NaiveDate) => ColumnIndex.Key.Resolved.WithSubindex,
  ): void {
    this.#overlaySlots.set($inner, slot);
    // A just-settled move keeps its chip + dim until its resting overlay
    // repaints (this pass); flush them a frame later so the drop has no gap.
    this.#ghost.flushPendingChip();
    const controller = this.#dragController;
    if (!controller || this.#overlaySources.has($inner)) return;
    this.#overlaySources.add($inner);

    const liveSlot = (): StickerSlot => this.#overlaySlots.get($inner) ?? slot;
    controller.registerSource($inner, {
      payload: () => {
        const slot = liveSlot();
        return StickerMoveDrag.KIND.payload({ cellId: slot.cellId });
      },
      onLift: (lift) => {
        // Grab measured from the artwork CENTER so the grabbed point stays
        // under the cursor; the chip starts exactly over the resting sticker
        // (liftClient = where the pointer grabbed it). The controller dims the
        // resting overlay in place while the floating chip stands in.
        const rect = $inner.getBoundingClientRect();
        const grab = {
          x: lift.grabOffset.x - rect.width / 2,
          y: lift.grabOffset.y - rect.height / 2,
        };
        const liftClient = {
          x: rect.left + lift.grabOffset.x,
          y: rect.top + lift.grabOffset.y,
        };
        this.#ghost.startMove(row, liveSlot(), grab, liftClient, $inner);
      },
      onMove: (e) => this.#ghost.moveTo(toClient(e.page), e.over == null),
      onSettle: () => this.#ghost.settle(),
      onTap: () =>
        this.#openOverlayDetails(
          $inner,
          liveSlot(),
          row,
          fixedContext,
          resolveColumn,
        ),
      onContextMenu: (e) =>
        this.#openOverlayContextMenu(
          $inner,
          liveSlot(),
          row,
          fixedContext,
          resolveColumn,
          e.client,
        ),
    });
  }

  /** Open a sticker's details from a tap on its overlay — anchored from the
   *  fixed-child render context and the mapper-resolved column key for the
   *  sticker's day. */
  #openOverlayDetails(
    $inner: HTMLElement,
    slot: StickerSlot,
    row: SheetRow.Joined.Handle,
    fixedContext: CalendarSubcolumn.FixedChildrenContext,
    resolveColumn: (date: NaiveDate) => ColumnIndex.Key.Resolved.WithSubindex,
  ): void {
    const windowed = this.#windowedForSlot(slot, fixedContext);
    if (!windowed) return;
    const item = this.#overlayItem(slot, row, null);
    if (!item) return;
    const context: ItemActionContext = {
      rowId: fixedContext.bandRowId,
      column: resolveColumn(windowed.fragment.date),
      windowed,
      row,
    };
    item.showDetails(context);
  }

  #openOverlayContextMenu(
    $inner: HTMLElement,
    slot: StickerSlot,
    row: SheetRow.Joined.Handle,
    fixedContext: CalendarSubcolumn.FixedChildrenContext,
    resolveColumn: (date: NaiveDate) => ColumnIndex.Key.Resolved.WithSubindex,
    client: Point,
  ): void {
    this.#clearContextMenuOverlay(null);
    const windowed = this.#windowedForSlot(slot, fixedContext);
    if (!windowed) return;
    this.#setContextMenuOverlay($inner);
    const item = this.#overlayItem(slot, row, () =>
      this.#clearContextMenuOverlay($inner)
    );
    if (!item) {
      this.#clearContextMenuOverlay($inner);
      return;
    }
    const context: ItemActionContext = {
      rowId: fixedContext.bandRowId,
      column: resolveColumn(windowed.fragment.date),
      windowed,
      row,
    };
    item.showContextMenu(context, this.#overlayOffset($inner, client));
  }

  #overlayItem(
    slot: StickerSlot,
    row: SheetRow.Joined.Handle,
    onContextMenuDismissed: Option<() => void>,
  ): Option<StickerGestureItem> {
    const delegate = this.#delegate;
    if (!delegate) return null;
    delegate.setContext(row);
    return new StickerGestureItem(slot, delegate, onContextMenuDismissed);
  }

  #setContextMenuOverlay($inner: HTMLElement): void {
    this.#contextMenuOverlay = $inner;
    StickerFixedChildren.setContextMenuOpen($inner, true);
  }

  #clearContextMenuOverlay($expected: Option<HTMLElement>): void {
    if ($expected != null && this.#contextMenuOverlay !== $expected) return;
    const $inner = this.#contextMenuOverlay;
    if (!$inner) return;
    StickerFixedChildren.setContextMenuOpen($inner, false);
    this.#contextMenuOverlay = null;
  }

  #windowedForSlot(
    slot: StickerSlot,
    fixedContext: CalendarSubcolumn.FixedChildrenContext,
  ): Option<DateFragment.Windowed> {
    const tz = fixedContext.renderedColumns.start.tz;
    const localStart = tz.toTz(slot.timeRange.start);
    const fragments = tz.date(localStart.date).dateFragments();
    const fragment = fragments.find((f) =>
      localStart.mse >= f.start.mse && localStart.mse < f.end.mse
    ) ?? fragments[0] ?? null;
    if (!fragment) return null;
    return new DateFragment.Windowed(
      fragment,
      undefined,
      fixedContext.validHours,
    );
  }

  #overlayOffset($inner: HTMLElement, client: Point): Point {
    const $wrap = $inner.parentElement instanceof HTMLElement
      ? $inner.parentElement
      : $inner;
    const rect = $wrap.getBoundingClientRect();
    const insetTop = Number.parseFloat(
      $wrap.getAttribute("data-inset-top") ?? "0",
    );
    return {
      x: client.x - rect.left,
      y: (Number.isFinite(insetTop) ? insetTop : 0) + client.y - rect.top,
    };
  }

  /** Subscribe a row's handle to re-emit overlays on cell changes
   *  (create/move/delete). Idempotent per row. */
  #observeRow(rowId: RowLocalId, variant: SourceRow): void {
    const key = rowId.toString();
    if (this.#rowSubs.has(key)) return;
    const lookupKey = variant.lookupKey;
    if (lookupKey == null) return;
    // Re-emit the overlay layer when the shared canonical store changes
    // (create / move / delete fire `set-custom` / `clear-custom`). The
    // rows-side store is the change signal now, not the RowCellCache.
    const unsub = variant.sheetReads.subscribeCanonicalCells(
      lookupKey,
      () => this.#handle?.rebind.rebindViewports(),
    );
    this.#rowSubs.set(key, unsub);
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeCell(): HTMLElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext): void {}

  onBindRow(): void {}
  onResize(): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  // -- Calendar (timeline-column day/week) path: one custom cell per
  //    sticker, painted as a fixed-child overlay. The overlay artwork is
  //    the sole move/details surface (`registerOverlaySource`); the
  //    subcolumn carries no spans, only the background create handler. --

  #calendarProvider: CalendarSubcolumn.Provider = ((
    self,
  ) => ({
    subcolumnFromRow(
      sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      const rowId = row.id;

      // Set context on the delegate so persistence uses this row's cell writer
      self.#delegate?.setContext(row);

      // Resolve a sticker's day to its column key for anchoring the
      // details popover opened from the overlay — the same mapper path
      // the event renderer uses.
      const mapper = sheet.indexRenderer.mapper;
      const resolveColumn = (
        date: NaiveDate,
      ): ColumnIndex.Key.Resolved.WithSubindex => {
        const key = ColumnIndex.Key.fromDate(date, "lower");
        return mapper
          .resolveKeyToDefault(key, "lower")
          .withSubindex(mapper.indexOfKey(key).subindex);
      };

      const slotsInRange = (
        range: DateTime.Range<Utc>,
      ): StickerSlot[] => {
        // Read datetime cells from the rows-side source row — the same
        // `canonicalCells` substrate weather/tasks read from, NOT the
        // lona-js RowCellCache. `performFetch` (via `loadData` below)
        // populates them.
        const variant = liveVariantOf(row);
        if (!(variant instanceof SourceRow)) return [];
        const slots: StickerSlot[] = [];
        // The rendered span is not a whole-day range, so filter the row's
        // one read door by start (inclusive) / end (exclusive) — the store's
        // own custom-range rule.
        for (const cell of variant.peekCells(null)) {
          if (cell.kind !== "timepoint") continue;
          if (cell.dtr.start.mse < range.start.mse || cell.dtr.start.mse >= range.end.mse) continue;
          const data = parseStickerData(cell.value);
          if (!data) continue;
          slots.push({
            id: `sticker-${rowId}-${cell.id}`,
            stickerId: data.stickerId,
            cellId: cell.id,
            timeRange: cell.dtr,
            message: data.message.length > 0 ? data.message : null,
            effect: data.effect,
            x: data.x,
          });
        }
        return slots;
      };
      const delegate = self.#delegate;

      return {
        rowId: RowId.fromLocalId(rowId),
        // The timed lane is empty; overlay painting still runs separately.
        // Its drop/create callbacks capture only these owners and the column
        // context, which CalendarDaysStrip includes in its own geometry key.
        readIdentity: () => [self, row, liveVariantOf(row), delegate, mapper],
        width: { type: "natural" },
        // Stickers draw only as fixed-child overlays (no in-grid spans), so
        // the subcolumn must NOT claim a layout lane — `stagger: false` keeps
        // it out of the day column's width-sharing so it never compresses a
        // sibling subcolumn (e.g. events) on the same day.
        stagger: false,
        loadData: async (date) => {
          // Fetch through the rows-side source row by its own lookupKey
          // into the shared `canonicalCells` store — the uniform
          // canonical-fetch path (weather/tasks do the same via
          // `variant.loadRange`). No RowCellCache, no per-plugin enroller.
          const variant = liveVariantOf(row);
          if (!(variant instanceof SourceRow)) return;
          const [pd] = date;
          await variant.ensureLoaded(pd.toRange(), null);
        },
        ...(delegate ? { createHandler: {
          onDragCreate: (timeRange, context) => {
            delegate.setContext(row);
            delegate.onDragCreate(timeRange, { ...context, row });
          },
          onClickCreate: (position, context) => {
            delegate.setContext(row);
            delegate.onClickCreate(position, { ...context, row });
          },
          updateGhost: (timeRange) => delegate.showNewGhost(timeRange),
          clearGhost: () => delegate.clearNewGhost(),
          isCreateRevealed: () => delegate.isCreateRevealed(),
          dismissCreate: () => delegate.dismissCreate(),
        } } : {}),
        makeLayout: () => {
          const $layout = CalendarColumnLayoutGroup.make();
          // Full-width background layer (the `stagger: false` intent): absolutely
          // positioned to fill the whole day column so the date-anchored sticker
          // overlays have a full-width box to position against, WITHOUT taking
          // flex width from the sibling events subcolumn (an in-flow, non-
          // shrinking sticker column would otherwise collapse events). The day
          // column (`CalendarDaysStrip` :host) is `position: relative`.
          $layout.style.position = "absolute";
          $layout.style.inset = "0";
          // This overlay carries NO in-grid spans (`makeSpansSync` and
          // `makeSpans` both answer none, below) and is stacked ON TOP of the
          // sibling events subcolumn (discovered after it, absolute inset:0). With default `pointer-events: auto` it would
          // swallow every click over the event area — the click hits this empty
          // layout-group, finds no cell, and bubbles to the column delegate,
          // which opens the *create* form instead of the clicked event's
          // details. Stickers take their pointer input from the separate
          // fixed-child overlay layer, and drop-target detection is rect-based
          // (`registerDropTarget` → geometry hit-test, not `elementsFromPoint`),
          // so making this box transparent to the pointer is safe and lets event
          // clicks reach the cells beneath.
          $layout.style.pointerEvents = "none";
          return $layout;
        },
        bindLayout: ($layout, context) => {
          self.#registerDropTarget($layout, context);
        },
        // Stickers carry no in-grid spans: their artwork and gestures are the
        // async `makeFixedChildren` overlay below, drawn in the sheet's
        // fixed-child layer, never strip spans. So both span members answer
        // none, the sync one included (why: `CalendarSubcolumn.makeSpansSync`).
        makeSpansSync: () => [],
        makeSpans: async () => [],
        makeFixedChildren: async (ctx) => {
          // The sticker artwork is painted in the sheet's fixed-child
          // layer (escaping the column's clip) and is the sticker's sole
          // pointer surface — `registerOverlaySource` makes each drawn
          // sticker a drag/tap handle. Subscribe once so a cell write
          // (create/move/delete) re-emits the overlays.
          const variant = liveVariantOf(row);
          if (variant instanceof SourceRow) self.#observeRow(rowId, variant);
          // Capture the handle for row-change overlay re-emits and targeted
          // fixed-child drag previews — this is the only place the sticker
          // plugin sees the sheet handle.
          self.#handle = ctx.handle;
          // Prune overlay drag sources whose element left the DOM since
          // the last pass (mirrors drop-target pruning).
          for (const $el of self.#overlaySources) {
            if (!$el.isConnected) {
              self.#dragController?.unregisterSource($el);
              self.#overlaySources.delete($el);
            }
          }
          const range = new DateTime.Range(
            ctx.renderedColumns.start.range.start,
            ctx.renderedColumns.end.range.end,
          );
          const inStore = slotsInRange(range);
          return StickerFixedChildren.makeSpecs(
            rowId.toString(),
            inStore,
            ctx,
            (slot, $inner) =>
              self.#registerOverlaySource(
                $inner,
                slot,
                row,
                ctx,
                resolveColumn,
              ),
          );
        },
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };
}
